import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import { useUserPersona } from '../context/UserPersonaContext';
import { useAuth } from '../context/AuthContext';
import registrationService from '../services/registrationService';
import { getApiBaseUrlWithFallback, REGISTRATION_BASE_URL } from '../config/environment';
import ChampionshipInvitations from '../components/ChampionshipInvitations';
import { getInvitations, getInvitationStatusDisplay } from '../services/qualifierInvitationService';
import { getEligibleCompetitionSummary } from '../utils/eligibilityDisplay';
import { isSeasonRegistrationOpen } from '../utils/seasonRegistration';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  IconButton,
  Collapse,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  EmojiEvents as TrophyIcon,
  CalendarToday as CalendarIcon,
  School as CoachIcon,
  Business as ClubIcon,
  Add as AddIcon,
  ArrowForward as ArrowForwardIcon,
  Schedule as PlannedIcon,
  CheckCircle as AttendedIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Flag as FlagIcon,
} from '@mui/icons-material';

// Parent Dashboard - shows skaters overview
function ParentDashboard({ skaters, onNavigateToSkater, onNavigateToSkaters, seasonRegistrationOpen }) {
  const navigate = useNavigate();
  const { showQualifierLink } = useAuth();

  if (!skaters || skaters.length === 0) {
    return (
      <Box>
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', borderRadius: 4, border: '1px solid', borderColor: 'divider' }}>
          <PersonIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Welcome to PNIC Scores!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Get started by adding your skaters to track their competitions and eligibility.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/skaters')}
            sx={{ borderRadius: 3, py: 1.5, px: 4 }}
          >
            Add Your First Skater
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      {/* Championship Invitations - Shows if any skaters have been invited */}
      <ChampionshipInvitations />

      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Skaters
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Quick overview of your skaters' progress and upcoming events
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {skaters.filter(s => s.isRegistered).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Registered Skaters
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'success.main' }}>
              {skaters.filter(s => s.isRegistered && s.eligibility?.isEligible).length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Eligible
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Skaters List */}
      <Grid container spacing={3}>
        {skaters.map((skater) => (
          <Grid item xs={12} sm={6} md={4} key={skater.id}>
            <Card 
              elevation={0} 
              sx={{ 
                borderRadius: 4, 
                border: '1px solid', 
                borderColor: skater.isRegistered ? 'divider' : 'warning.main',
                cursor: 'pointer',
                transition: 'all 0.2s',
                opacity: skater.isRegistered ? 1 : 0.85,
                '&:hover': { 
                  borderColor: 'primary.main',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                  opacity: 1,
                }
              }}
              onClick={() => onNavigateToSkater(skater.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Avatar sx={{ bgcolor: skater.isRegistered ? 'primary.main' : 'grey.400', width: 48, height: 48, fontWeight: 700 }}>
                    {(skater.firstName || 'S')[0]}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {skater.firstName} {skater.lastName}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {skater.isRegistered ? (
                        <Chip 
                          label="Registered" 
                          size="small" 
                          color="success"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      ) : (
                        <Chip 
                          label="Not Registered" 
                          size="small" 
                          color="warning"
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      )}
                      {skater.currentLevel && (
                        <Chip 
                          label={skater.currentLevel} 
                          size="small" 
                          variant="outlined"
                          sx={{ height: 22, fontSize: '0.75rem' }}
                        />
                      )}
                    </Box>
                  </Box>
                </Box>
                {skater.isRegistered ? (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box>
                        {(() => {
                          const summary = getEligibleCompetitionSummary(skater.eligibility);
                          return (
                            <Tooltip title={summary.detail || `${summary.counting} of ${summary.attended || 3} competitions count toward championship`}>
                              <Box>
                                <Typography variant="caption" color="text.secondary">
                                  {summary.label}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600, color: summary.excluded > 0 ? 'warning.dark' : 'text.primary' }}
                                >
                                  {summary.primary}
                                </Typography>
                                {summary.excludedLabel && (
                                  <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', fontWeight: 600 }}>
                                    {summary.excludedLabel}
                                  </Typography>
                                )}
                              </Box>
                            </Tooltip>
                          );
                        })()}
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          Points
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                          {skater.totalPoints || 0}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={skater.eligibility?.isEligible ? 'Eligible' : 'Not Yet'}
                        color={skater.eligibility?.isEligible ? 'success' : 'warning'}
                        sx={{ height: 24, fontSize: '0.7rem' }}
                      />
                    </Box>
                  </>
                ) : (
                  <Box sx={{ mt: 1, p: 1.5, bgcolor: 'warning.50', borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 500 }}>
                      {seasonRegistrationOpen ? 'Register to track eligibility' : 'View skater history'}
                    </Typography>
                  </Box>
                )}
              </CardContent>
              <CardActions sx={{ px: 2, pb: 2 }}>
                <Button 
                  size="small" 
                  endIcon={<ArrowForwardIcon />}
                  sx={{ ml: 'auto' }}
                >
                  {skater.isRegistered || !seasonRegistrationOpen ? 'View Details' : 'Register Now'}
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
        
        {/* Add Skater Card */}
        <Grid item xs={12} sm={6} md={4}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 4, 
              border: '2px dashed', 
              borderColor: 'divider',
              cursor: 'pointer',
              height: '100%',
              minHeight: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s',
              '&:hover': { 
                borderColor: 'primary.main',
                bgcolor: 'primary.50'
              }
            }}
            onClick={onNavigateToSkaters}
          >
            <CardContent sx={{ textAlign: 'center' }}>
              <AddIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                Add Skater
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Actions */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/competitions')}
              startIcon={<CalendarIcon />}
              sx={{ borderRadius: 3, py: 1.5, justifyContent: 'flex-start' }}
            >
              View Competitions
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/registration')}
              startIcon={<TrophyIcon />}
              sx={{ borderRadius: 3, py: 1.5, justifyContent: 'flex-start' }}
            >
              Season Registration
            </Button>
          </Grid>
          {showQualifierLink && (
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => navigate('/qualifiers')}
                startIcon={<FlagIcon />}
                sx={{ borderRadius: 3, py: 1.5, justifyContent: 'flex-start' }}
              >
                View Qualifiers
              </Button>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

// Coach Dashboard - shows coached skaters and their events
function CoachDashboardView({ onNavigateToSkater }) {
  const { getIdToken } = useFirebaseAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [coachedSkaters, setCoachedSkaters] = useState([]);
  const [skaterEligibility, setSkaterEligibility] = useState({});
  const [competitions, setCompetitions] = useState([]);
  const [expandedSkaters, setExpandedSkaters] = useState(new Set());
  const [invitationsBySkater, setInvitationsBySkater] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        throw new Error('Unable to get authentication token');
      }

      const baseUrl = await getApiBaseUrlWithFallback();

      // Load coached skaters
      const skatersResponse = await fetch(`${REGISTRATION_BASE_URL}/skaters/coached`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      if (!skatersResponse.ok) {
        throw new Error('Failed to load coached skaters');
      }
      
      const skatersData = await skatersResponse.json();
      const raw = skatersData.skaters || [];
      // Deduplicate by id (coach+parent may see same child twice)
      const seen = new Set();
      const skaters = raw.filter((s) => {
        if (seen.has(s.id)) return false;
        seen.add(s.id);
        return true;
      });
      setCoachedSkaters(skaters);

      try {
        const inviteData = await getInvitations({ mySkaters: true });
        const inviteMap = {};
        (inviteData.invitations || []).forEach((invite) => {
          if (invite.skaterId) inviteMap[invite.skaterId] = invite;
          const nameKey = `${String(invite.firstName || '').trim().toLowerCase()}|${String(invite.lastName || '').trim().toLowerCase()}`;
          if (nameKey !== '|') inviteMap[nameKey] = invite;
        });
        setInvitationsBySkater(inviteMap);
      } catch (inviteError) {
        console.error('Error loading championship invitations for coach dashboard:', inviteError);
        setInvitationsBySkater({});
      }

      // Load competitions
      const competitionsResponse = await fetch(`${baseUrl}/competitions`);
      if (competitionsResponse.ok) {
        const compsData = await competitionsResponse.json();
        const now = new Date();
        const upcomingComps = compsData
          .filter((c) => {
            const compDate = c.date?._seconds 
              ? new Date(c.date._seconds * 1000) 
              : new Date(c.date || c.startDate);
            return compDate > now;
          })
          .sort((a, b) => {
            const aDate = a.date?._seconds ? a.date._seconds : new Date(a.date || a.startDate).getTime() / 1000;
            const bDate = b.date?._seconds ? b.date._seconds : new Date(b.date || b.startDate).getTime() / 1000;
            return aDate - bDate;
          });
        setCompetitions(upcomingComps);
      }

      // Load eligibility for each skater
      const eligibilityData = {};
      await Promise.all(
        skaters.map(async (skater) => {
          try {
            const response = await fetch(`${baseUrl}/skaters/${skater.id}/eligibility`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              const data = await response.json();
              eligibilityData[skater.id] = {
                ...data,
                attendedCompIds: new Set((data.competitionsAttended || []).map((c) => c.competitionId)),
                plannedCompIds: new Set(data.plannedEvents || []),
              };
            }
          } catch (err) {
            console.error(`Error loading eligibility for skater ${skater.id}:`, err);
          }
        })
      );
      setSkaterEligibility(eligibilityData);
    } catch (err) {
      console.error('Error loading coach dashboard:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getIdToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const toggleSkaterExpanded = (skaterId) => {
    setExpandedSkaters((prev) => {
      const next = new Set(prev);
      if (next.has(skaterId)) {
        next.delete(skaterId);
      } else {
        next.add(skaterId);
      }
      return next;
    });
  };

  // Aggregate: which competitions have skaters planning to attend
  const competitionAttendance = competitions.map((comp) => {
    const attending = [];
    const planned = [];
    
    coachedSkaters.forEach((skater) => {
      const eligibility = skaterEligibility[skater.id];
      if (!eligibility) return;
      
      if (eligibility.attendedCompIds.has(comp.id)) {
        attending.push(skater);
      } else if (eligibility.plannedCompIds.has(comp.id)) {
        planned.push(skater);
      }
    });
    
    return {
      ...comp,
      attending,
      planned,
      hasActivity: attending.length > 0 || planned.length > 0,
    };
  }).filter((c) => c.hasActivity);

  const unpaidSkaters = coachedSkaters.filter((skater) => !skater.paid);
  const paidSkatersCount = coachedSkaters.length - unpaidSkaters.length;

  const formatDate = (dateObj) => {
    if (!dateObj) return 'TBD';
    let date;
    if (dateObj._seconds) {
      // Firestore Timestamp - extract UTC date parts to avoid timezone shift
      const utcDate = new Date(dateObj._seconds * 1000);
      date = new Date(utcDate.getUTCFullYear(), utcDate.getUTCMonth(), utcDate.getUTCDate());
    } else {
      date = new Date(dateObj);
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ borderRadius: 2 }}>
        {error}
        <Button onClick={loadData} sx={{ ml: 2 }} size="small">
          Retry
        </Button>
      </Alert>
    );
  }

  if (coachedSkaters.length === 0) {
    return (
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
              display: 'flex',
              boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
            }}>
              <CoachIcon sx={{ color: 'white', fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                Coach Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                As a coach, you can track your students' competition results and progress. Parents register and pay for their own skaters.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* How it works */}
        <Alert 
          severity="info" 
          sx={{ mb: 4, borderRadius: 3 }}
          icon={<PersonIcon />}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>How it works:</Typography>
          <Typography variant="body2">
            When parents add a skater and select you as their coach, that skater will automatically appear in your "Coached Skaters" list. 
            You can also link to existing skaters by their USFS membership ID.
          </Typography>
        </Alert>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 4 }}>
          <Button
            variant="contained"
            onClick={() => navigate('/skaters')}
            sx={{ 
              borderRadius: 3, 
              px: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            }}
          >
            View Skaters
          </Button>
          <Button
            variant="outlined"
            onClick={() => navigate('/profile')}
            sx={{ borderRadius: 3 }}
          >
            Edit Profile
          </Button>
        </Box>

        {/* Upcoming Competitions - useful even without skaters */}
        {competitions.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
              Upcoming Competitions
            </Typography>
            <Grid container spacing={2}>
              {competitions.slice(0, 4).map((comp) => (
                <Grid item xs={12} sm={6} key={comp.id}>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3, cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                    onClick={() => navigate('/competitions')}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {comp.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(comp.date || comp.startDate)}
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button 
                variant="text" 
                onClick={() => navigate('/competitions')}
                sx={{ textTransform: 'none' }}
              >
                View All Competitions →
              </Button>
            </Box>
          </Box>
        )}

        {/* Are you also a parent? */}
        <Paper elevation={0} sx={{ p: 3, borderRadius: 4, border: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
            Are you also a parent?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            If you have children who skate and need to register them for the season, update your profile to indicate you're also a parent. 
            This will allow you to add new skaters and register them for competitions.
          </Typography>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/profile')}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Update Profile Settings →
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <ChampionshipInvitations />

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
            Coach Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track your skaters' progress and upcoming events
          </Typography>
        </Box>
        <IconButton 
          onClick={loadData} 
          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'primary.main' }}>
              {coachedSkaters.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Coached Skaters
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'success.main' }}>
              {paidSkatersCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Paid
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: unpaidSkaters.length ? 'warning.main' : 'text.secondary' }}>
              {unpaidSkaters.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Unpaid
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: 'success.main' }}>
              {competitionAttendance.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Events with Plans
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {unpaidSkaters.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {unpaidSkaters.length} skater{unpaidSkaters.length === 1 ? '' : 's'} registered but unpaid
          </Typography>
          <Typography variant="body2">
            {unpaidSkaters.map((skater) => `${skater.firstName} ${skater.lastName}`.trim()).join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Upcoming Events with Skaters */}
      {competitionAttendance.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
            Upcoming Events with Your Skaters
          </Typography>
          <Grid container spacing={2}>
            {competitionAttendance.slice(0, 4).map((comp) => (
              <Grid item xs={12} sm={6} key={comp.id}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    borderColor: comp.attending.length > 0 ? 'success.main' : 'primary.main',
                    bgcolor: comp.attending.length > 0 ? 'success.50' : 'primary.50',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    {comp.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {formatDate(comp.date || comp.startDate)}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {comp.attending.map((skater) => (
                      <Chip
                        key={skater.id}
                        size="small"
                        icon={<AttendedIcon sx={{ fontSize: '14px !important' }} />}
                        label={`${skater.firstName} ✓`}
                        onClick={() => onNavigateToSkater(skater.id)}
                        sx={{ 
                          height: 24, 
                          fontSize: '0.7rem',
                          bgcolor: 'success.100',
                          color: 'success.dark',
                          cursor: 'pointer',
                          '& .MuiChip-icon': { color: 'success.main' }
                        }}
                      />
                    ))}
                    {comp.planned.map((skater) => (
                      <Chip
                        key={skater.id}
                        size="small"
                        icon={<PlannedIcon sx={{ fontSize: '14px !important' }} />}
                        label={skater.firstName}
                        onClick={() => onNavigateToSkater(skater.id)}
                        sx={{ 
                          height: 24, 
                          fontSize: '0.7rem',
                          bgcolor: 'primary.100',
                          color: 'primary.dark',
                          cursor: 'pointer',
                          '& .MuiChip-icon': { color: 'primary.main' }
                        }}
                      />
                    ))}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Skaters List */}
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
        Your Skaters
      </Typography>
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <List sx={{ bgcolor: 'grey.50' }}>
          {coachedSkaters.map((skater, index) => {
            const eligibility = skaterEligibility[skater.id];
            const isExpanded = expandedSkaters.has(skater.id);
            const plannedCount = eligibility?.plannedCompIds?.size || 0;
            const attendedCount = eligibility?.attendedCompIds?.size || 0;
            const invitation =
              invitationsBySkater[skater.id] ||
              invitationsBySkater[
                `${String(skater.firstName || '').trim().toLowerCase()}|${String(skater.lastName || '').trim().toLowerCase()}`
              ];
            const invitationDisplay = invitation ? getInvitationStatusDisplay(invitation.status) : null;

            return (
              <React.Fragment key={skater.id}>
                <ListItem
                  sx={{
                    borderBottom: index < coachedSkaters.length - 1 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'grey.100' },
                  }}
                  onClick={() => toggleSkaterExpanded(skater.id)}
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={(e) => {
                          e.stopPropagation();
                          onNavigateToSkater(skater.id);
                        }}
                        sx={{ borderRadius: 2, fontSize: '0.7rem' }}
                      >
                        View
                      </Button>
                      <IconButton edge="end" size="small">
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                      {(skater.firstName || 'S')[0]}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {skater.firstName} {skater.lastName}
                        </Typography>
                        {skater.currentLevel && (
                          <Chip label={skater.currentLevel} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        <Chip
                          label={skater.paid ? 'Paid' : 'Unpaid'}
                          color={skater.paid ? 'success' : 'warning'}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                        {invitationDisplay && (
                          <Chip
                            label={invitationDisplay.label}
                            color={invitationDisplay.color}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Tooltip title="Season qualification points (top 3 scores from current season)" arrow placement="top">
                          <Chip
                            size="small"
                            icon={<TrophyIcon sx={{ fontSize: '12px !important' }} />}
                            label={`${eligibility?.eligibility?.totalPoints ?? 0} pts`}
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              ...((eligibility?.eligibility?.totalPoints ?? 0) > 0
                                ? { bgcolor: 'primary.50', color: 'primary.dark' }
                                : { bgcolor: 'grey.200', color: 'text.secondary' }),
                            }}
                          />
                        </Tooltip>
                        {attendedCount > 0 && (
                          <Chip
                            size="small"
                            icon={<AttendedIcon sx={{ fontSize: '12px !important' }} />}
                            label={`${attendedCount} attended`}
                            sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'success.50', color: 'success.dark' }}
                          />
                        )}
                        {plannedCount > 0 && (
                          <Chip
                            size="small"
                            icon={<PlannedIcon sx={{ fontSize: '12px !important' }} />}
                            label={`${plannedCount} planned`}
                            sx={{ height: 20, fontSize: '0.65rem', bgcolor: 'primary.50', color: 'primary.dark' }}
                          />
                        )}
                        {attendedCount === 0 && plannedCount === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            No events planned
                          </Typography>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 3, py: 2, bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider' }}>
                    {eligibility ? (
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                            <Box>
                              {(() => {
                                const summary = getEligibleCompetitionSummary(eligibility.eligibility);
                                return (
                                  <Tooltip title={summary.detail || `${summary.counting} of ${summary.attended || 3} competitions count toward championship`}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">{summary.label}</Typography>
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: summary.excluded > 0 ? 'warning.dark' : 'text.primary' }}>
                                        {summary.primary} count
                                      </Typography>
                                      {summary.excludedLabel && (
                                        <Typography variant="caption" sx={{ color: 'warning.dark', display: 'block', fontWeight: 600 }}>
                                          {summary.excludedLabel}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Tooltip>
                                );
                              })()}
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Total Points</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {eligibility.eligibility?.totalPoints || 0}
                              </Typography>
                            </Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary">Status</Typography>
                              <Chip
                                size="small"
                                label={eligibility.eligibility?.isEligible ? 'Eligible' : 'Not Yet'}
                                color={eligibility.eligibility?.isEligible ? 'success' : 'warning'}
                                sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Loading eligibility data...
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </React.Fragment>
            );
          })}
        </List>
      </Paper>

      {/* Quick Actions */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2, textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
          Quick Actions
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/skaters')}
              startIcon={<PersonIcon />}
              sx={{ borderRadius: 3, py: 1.5, justifyContent: 'flex-start' }}
            >
              Manage Skaters
            </Button>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate('/competitions')}
              startIcon={<CalendarIcon />}
              sx={{ borderRadius: 3, py: 1.5, justifyContent: 'flex-start' }}
            >
              View Competitions
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

// Interclub Admin Dashboard placeholder
function InterclubAdminDashboard() {
  const navigate = useNavigate();

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Interclub Admin Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your interclub competitions and skaters
        </Typography>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => navigate('/interclub-admin')}
            startIcon={<ClubIcon />}
            sx={{ borderRadius: 3, py: 2, justifyContent: 'flex-start' }}
          >
            Go to Interclub Admin Panel
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}

// Main Dashboard component
export default function Dashboard() {
  const navigate = useNavigate();
  const { user, getIdToken, loading: authLoading, isInterclubAdmin } = useFirebaseAuth();
  const { persona, profile, skaters: personaSkaters, loading: personaLoading } = useUserPersona();
  
  const [skaters, setSkaters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seasonRegistrationOpen, setSeasonRegistrationOpen] = useState(false);

  // Load skater data with eligibility and registration status
  const loadSkaterData = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) return;

      const baseUrl = await getApiBaseUrlWithFallback();
      const skatersData = await registrationService.getSkaters(token);
      
      // Load current season to check registrations
      let currentSeasonId = null;
      let registrations = [];
      try {
        const seasonsResponse = await fetch(`${baseUrl}/seasons`);
        if (seasonsResponse.ok) {
          const seasonsData = await seasonsResponse.json();
          const now = new Date();
          const byIsCurrent = seasonsData.find((s) => s.isCurrent === true);
          const byDateRange = seasonsData.find((s) => {
            const startDate = s.startDate?._seconds ? new Date(s.startDate._seconds * 1000) : new Date(s.startDate);
            const endDate = s.endDate?._seconds ? new Date(s.endDate._seconds * 1000) : new Date(s.endDate);
            return now >= startDate && now <= endDate;
          });
          const currentSeason = byIsCurrent || byDateRange;
          if (currentSeason) {
            currentSeasonId = currentSeason.id;
            setSeasonRegistrationOpen(isSeasonRegistrationOpen(currentSeason));
            registrations = await registrationService.getSeasonRegistrations(currentSeasonId, token);
          }
        }
      } catch (err) {
        console.error('Error loading season/registrations:', err);
      }
      
      // Build a set of registered skater IDs
      const registeredSkaterIds = new Set(registrations.map(r => r.skaterId));
      
      // Load eligibility for each skater (same season as registration check when possible)
      const skatersWithEligibility = await Promise.all(
        skatersData.map(async (skater) => {
          const isRegistered = registeredSkaterIds.has(skater.id);
          try {
            const eligUrl =
              `${baseUrl}/skaters/${skater.id}/eligibility` +
              (currentSeasonId ? `?seasonId=${encodeURIComponent(currentSeasonId)}` : '');
            const response = await fetch(eligUrl, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
              const eligibility = await response.json();
              return {
                ...skater,
                isRegistered,
                eligibility: eligibility.eligibility,
                competitionCount: eligibility.eligibility?.countingCompetitions
                  ?? eligibility.eligibility?.uniqueClubsCompeted
                  ?? eligibility.eligibility?.totalCompetitions
                  ?? 0,
                totalPoints: eligibility.eligibility?.totalPoints || 0,
              };
            }
          } catch (err) {
            console.error(`Error loading eligibility for skater ${skater.id}:`, err);
          }
          return { ...skater, isRegistered };
        })
      );

      setSkaters(skatersWithEligibility);
    } catch (err) {
      console.error('Error loading skater data:', err);
    } finally {
      setLoading(false);
    }
  }, [user, getIdToken]);

  useEffect(() => {
    if (!authLoading && !personaLoading && user) {
      loadSkaterData();
    } else if (!authLoading && !user) {
      navigate('/');
    }
  }, [authLoading, personaLoading, user, navigate, loadSkaterData]);

  const handleNavigateToSkater = (skaterId) => {
    navigate(`/skaters/${skaterId}`);
  };

  const handleNavigateToSkaters = () => {
    navigate('/skaters');
  };

  if (authLoading || personaLoading || loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  // Determine which dashboard to show based on persona and roles
  // Coach must be approved to see coach dashboard
  const isApprovedCoach = (profile?.isCoach || profile?.roles?.includes('coach')) && 
    profile?.approvalStatus?.coach === 'approved';
  const isInterclub = isInterclubAdmin?.();

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      {/* Show Interclub Admin Dashboard if user is an interclub admin */}
      {isInterclub && <InterclubAdminDashboard />}
      
      {/* Show Coach Dashboard if user is an approved coach (and not interclub admin taking priority) */}
      {!isInterclub && isApprovedCoach && (
        <CoachDashboardView onNavigateToSkater={handleNavigateToSkater} />
      )}
      
      {/* Show Parent/Skater Dashboard for everyone else */}
      {!isInterclub && !isApprovedCoach && (
        <ParentDashboard 
          skaters={skaters} 
          onNavigateToSkater={handleNavigateToSkater}
          onNavigateToSkaters={handleNavigateToSkaters}
          seasonRegistrationOpen={seasonRegistrationOpen}
        />
      )}
    </Box>
  );
}
